import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import { useMsal } from '@azure/msal-react';
import useFetchSensors, { FetchSensorsParams } from './useFetchSensors';
import useFetchInventorySummary from './useFetchInventorySummary';
import { msalConfig } from '../auth/authConfig';
import { errorHandler } from '../utils/helpers';
import useUserStore from '../store/userStore';
import { acquireToken } from '../utils/auth';

interface DeleteSensorRequest {
  id: string | number;
  token: string | null;
  setError: (data: any) => any;
  setLoading: (data: any) => any;
  fetchSensors: (params: FetchSensorsParams) => void;
  fetchInventorySummary: () => void;
  userId: string | number;
};

const deleteSensorRequest = async ({
    token,
    setError,
    setLoading,
    fetchSensors,
    fetchInventorySummary,
    id,
    userId,
  }: DeleteSensorRequest) => {
  try {
    const response = await axios.delete(`${window.__env.REACT_APP_API_URL || process.env.REACT_APP_API_URL}/Sensor/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json, text/plain, */*',
        userId,
      },
    });
    if (response.status === 200) {
      NotificationManager.success('Sensor is successfully deleted');
    }
  } catch (error) {
    errorHandler(error, setError);
  } finally {
    fetchSensors({ page: 1 });
    fetchInventorySummary();
    setLoading(false);
  }
};

interface DeleteSensor {
  deleteSensor: (id: string | number) => void;
  loading: boolean;
  error: Error | null;
};

const useDeleteSensor = (): DeleteSensor => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { fetchSensors } = useFetchSensors();
  const { fetchInventorySummary } = useFetchInventorySummary();

  const { user } = useUserStore();

  const deleteSensor = async (id) => {
    if (accounts.length > 0) {
      const accessTokenRequest = {
        scopes: [`${msalConfig.auth.clientId}/.default`],
        account: accounts[0],
      };

      if (!user) return navigate('/sign-in');

      try {
        const accessToken = await acquireToken(instance, accessTokenRequest);
        deleteSensorRequest({
          id,
          token: accessToken,
          setError,
          fetchSensors,
          fetchInventorySummary,
          setLoading,
          userId: user.id,
        });
      } catch (error) {
        errorHandler(error, setError);
      }
    } else {
      navigate('/sign-in');
    }
  }

  return { deleteSensor, loading, error };
};

export default useDeleteSensor;
